/**
 * Article registry for the help centre.
 *
 * Each entry: slug → { title, summary, roles[], component }.
 * `roles` lists every role this article applies to — the same article
 * can show up under multiple role landing pages (and side-navs).
 *
 * The article body itself is role-agnostic; permission differences
 * are noted inline (e.g. "School admins can do this for any classroom;
 * teachers only for ones they're assigned to").
 */

import GettingStartedSchoolAdmin from "./articles/gettingStartedSchoolAdmin";
import ConnectingSquare from "./articles/connectingSquare";
import CreatingACampaign from "./articles/creatingACampaign";
import PayingForYourCampaign from "./articles/payingForYourCampaign";
import SettingSchoolMilestones from "./articles/settingSchoolMilestones";
import SettingStudentPrizes from "./articles/settingStudentPrizes";
import SettingClassroomGoalsAndMilestones from "./articles/settingClassroomGoalsAndMilestones";
import ImportingRosterCsv from "./articles/importingRosterCsv";
import AddingClassroomsManually from "./articles/addingClassroomsManually";
import InvitingTeachers from "./articles/invitingTeachers";
import ManagingMembers from "./articles/managingMembers";
import PrintingTakeHomeSheets from "./articles/printingTakeHomeSheets";
import RotatingActivationCodes from "./articles/rotatingActivationCodes";
import SchoolAdminDashboard from "./articles/schoolAdminDashboard";
import TrackingDonations from "./articles/trackingDonations";
import RefundsViaSquare from "./articles/refundsViaSquare";
import CampaignLifecycle from "./articles/campaignLifecycle";
import AfterTheCampaign from "./articles/afterTheCampaign";
import SchoolSettings from "./articles/schoolSettings";
import FeesAndPricing from "./articles/feesAndPricing";

import GettingStartedTeacher from "./articles/gettingStartedTeacher";
import TeacherClassroomDashboard from "./articles/teacherClassroomDashboard";
import LoggingClassroomMinutes from "./articles/loggingClassroomMinutes";
import LoggingSoloMinutes from "./articles/loggingSoloMinutes";
import UndoingMinuteBatches from "./articles/undoingMinuteBatches";
import HandingOutStudentPrizes from "./articles/handingOutStudentPrizes";

import ActivatingYourChild from "./articles/activatingYourChild";
import ParentDashboard from "./articles/parentDashboard";
import LoggingMinutesAtHome from "./articles/loggingMinutesAtHome";
import SharingWithFamily from "./articles/sharingWithFamily";
import SettingPersonalGoals from "./articles/settingPersonalGoals";
import MultipleChildren from "./articles/multipleChildren";
import ParentMagicLinkSignin from "./articles/parentMagicLinkSignin";
import LeaderboardsAndPrivacy from "./articles/leaderboardsAndPrivacy";

import MakingADonation from "./articles/makingADonation";
import CoveringProcessingFees from "./articles/coveringProcessingFees";
import AnonymousDonationsAndMessages from "./articles/anonymousDonationsAndMessages";
import DonorReceiptsAndRefunds from "./articles/donorReceiptsAndRefunds";
import DonatingWithoutAnAccount from "./articles/donatingWithoutAnAccount";

export const ARTICLES = {
  // ── School admin ─────────────────────────────────────────
  "getting-started-school-admin": {
    slug: "getting-started-school-admin",
    title: "Getting your school set up",
    summary: "Sign up, start a free trial, and follow the setup guide to launch day.",
    roles: ["school_admins"],
    component: GettingStartedSchoolAdmin,
  },
  "connecting-square": {
    slug: "connecting-square",
    title: "Connecting your Square account",
    summary: "How donations land directly in your school's bank account, not ours.",
    roles: ["school_admins"],
    component: ConnectingSquare,
  },
  "creating-a-campaign": {
    slug: "creating-a-campaign",
    title: "Creating your campaign",
    summary: "Pick dates, set goals, and get your campaign approved to go live.",
    roles: ["school_admins"],
    component: CreatingACampaign,
  },
  "setting-school-milestones": {
    slug: "setting-school-milestones",
    title: "School-wide rewards (everyone earns them together)",
    summary: "Pizza party, principal-gets-pied — things the whole school earns when they hit a goal together.",
    roles: ["school_admins"],
    component: SettingSchoolMilestones,
  },
  "setting-student-prizes": {
    slug: "setting-student-prizes",
    title: "Student prizes (each student earns their own)",
    summary: "\"Read 500 minutes, get a fidget spinner.\" Every student earns their own.",
    roles: ["school_admins"],
    component: SettingStudentPrizes,
  },
  "setting-classroom-goals-and-milestones": {
    slug: "setting-classroom-goals-and-milestones",
    title: "Classroom goals and rewards",
    summary: "A goal nudges your room toward a number; a reward is something they earn together when they hit it.",
    roles: ["school_admins", "teachers"],
    component: SettingClassroomGoalsAndMilestones,
  },
  "importing-roster-csv": {
    slug: "importing-roster-csv",
    title: "Adding your students from a spreadsheet",
    summary: "Add every classroom and student in one upload. Activation codes are made for you.",
    roles: ["school_admins"],
    component: ImportingRosterCsv,
  },
  "adding-classrooms-manually": {
    slug: "adding-classrooms-manually",
    title: "Adding classrooms and students by hand",
    summary: "For a small school or a single new student — skip the spreadsheet and just type it in.",
    roles: ["school_admins"],
    component: AddingClassroomsManually,
  },
  "inviting-teachers": {
    slug: "inviting-teachers",
    title: "Inviting teachers",
    summary: "One at a time, or all at once with a quick spreadsheet.",
    roles: ["school_admins"],
    component: InvitingTeachers,
  },
  "managing-members": {
    slug: "managing-members",
    title: "Managing school admins and teachers",
    summary: "Change someone's role, remove someone who's left, see everyone on your team.",
    roles: ["school_admins"],
    component: ManagingMembers,
  },
  "printing-take-home-sheets": {
    slug: "printing-take-home-sheets",
    title: "Printing take-home sheets",
    summary: "One sheet per student, one PDF per classroom (or the whole school in one go).",
    roles: ["school_admins", "teachers"],
    component: PrintingTakeHomeSheets,
  },
  "rotating-activation-codes": {
    slug: "rotating-activation-codes",
    title: "Replacing a lost take-home sheet",
    summary: "A sheet got lost or ended up in the wrong hands. Swap the code for a new one.",
    roles: ["school_admins", "teachers"],
    component: RotatingActivationCodes,
  },
  "school-admin-dashboard": {
    slug: "school-admin-dashboard",
    title: "Your dashboard",
    summary: "What's on each part of the page, and how it changes as the campaign moves through its stages.",
    roles: ["school_admins"],
    component: SchoolAdminDashboard,
  },
  "tracking-donations": {
    slug: "tracking-donations",
    title: "Tracking donations",
    summary: "Where to find every donation and what each status means.",
    roles: ["school_admins"],
    component: TrackingDonations,
  },
  "refunds-via-square": {
    slug: "refunds-via-square",
    title: "Refunding a donation",
    summary: "Refunds happen in Square, not here — and the donation updates back here automatically.",
    roles: ["school_admins"],
    component: RefundsViaSquare,
  },
  "campaign-lifecycle": {
    slug: "campaign-lifecycle",
    title: "The four stages of a campaign",
    summary: "What changes at each stage, and what your school can and can't do in each.",
    roles: ["school_admins", "teachers"],
    component: CampaignLifecycle,
  },
  "after-the-campaign": {
    slug: "after-the-campaign",
    title: "After the campaign ends",
    summary: "Late donations, the final results, and what stays put afterwards.",
    roles: ["school_admins", "teachers"],
    component: AfterTheCampaign,
  },
  "school-settings": {
    slug: "school-settings",
    title: "School settings",
    summary: "Name, address, contact info — the things that show up on take-home sheets and donor pages.",
    roles: ["school_admins"],
    component: SchoolSettings,
  },
  "paying-for-your-campaign": {
    slug: "paying-for-your-campaign",
    title: "Paying for your campaign",
    summary: "What the free trial includes, what a campaign costs, and how to pay so it can go live.",
    roles: ["school_admins"],
    component: PayingForYourCampaign,
  },
  "fees-and-pricing": {
    slug: "fees-and-pricing",
    title: "How fees and pricing work",
    summary: "A flat fee per campaign, no percentage of what you raise, and how Square's small card fee fits in.",
    roles: ["school_admins"],
    component: FeesAndPricing,
  },

  // ── Teacher ──────────────────────────────────────────────
  "getting-started-teacher": {
    slug: "getting-started-teacher",
    title: "Getting started as a teacher",
    summary: "Sign in (no password needed), find your classroom, and learn what you can and can't do.",
    roles: ["teachers"],
    component: GettingStartedTeacher,
  },
  "teacher-classroom-dashboard": {
    slug: "teacher-classroom-dashboard",
    title: "Your classroom dashboard",
    summary: "Live numbers, leaderboards, school-wide context — all on one page.",
    roles: ["teachers"],
    component: TeacherClassroomDashboard,
  },
  "logging-classroom-minutes": {
    slug: "logging-classroom-minutes",
    title: "Logging the class's reading time",
    summary: "One quick form gets your whole classroom logged in five seconds.",
    roles: ["school_admins", "teachers"],
    component: LoggingClassroomMinutes,
  },
  "logging-solo-minutes": {
    slug: "logging-solo-minutes",
    title: "Logging for one student",
    summary: "When a student reads on their own and you want to log just their time.",
    roles: ["school_admins", "teachers"],
    component: LoggingSoloMinutes,
  },
  "undoing-minute-batches": {
    slug: "undoing-minute-batches",
    title: "Undoing a reading log",
    summary: "Logged the wrong number, or the wrong day? Roll it back with one click.",
    roles: ["school_admins", "teachers"],
    component: UndoingMinuteBatches,
  },
  "handing-out-student-prizes": {
    slug: "handing-out-student-prizes",
    title: "Handing out student prizes",
    summary: "Mark each prize as handed out, and see who's still waiting for theirs.",
    roles: ["school_admins", "teachers"],
    component: HandingOutStudentPrizes,
  },

  // ── Parent ───────────────────────────────────────────────
  "activating-your-child": {
    slug: "activating-your-child",
    title: "Signing your child up",
    summary: "Scan the QR code on the take-home sheet, type in your email, and you're in.",
    roles: ["parents"],
    component: ActivatingYourChild,
  },
  "parent-magic-link-signin": {
    slug: "parent-magic-link-signin",
    title: "Signing in (no password to remember)",
    summary: "Type your email, click the link in your inbox, you're in. Stay signed in for 30 days.",
    roles: ["parents"],
    component: ParentMagicLinkSignin,
  },
  "parent-dashboard": {
    slug: "parent-dashboard",
    title: "Your dashboard",
    summary: "Each child you're following, their progress, and the buttons you'll use most.",
    roles: ["parents"],
    component: ParentDashboard,
  },
  "logging-minutes-at-home": {
    slug: "logging-minutes-at-home",
    title: "Logging reading from home",
    summary: "After bedtime stories, log it in 10 seconds from your phone.",
    roles: ["parents"],
    component: LoggingMinutesAtHome,
  },
  "sharing-with-family":  {
    slug: "sharing-with-family",
    title: "Sharing the donation page with family",
    summary: "One tap to share — grandparents, aunts, family group chats.",
    roles: ["parents"],
    component: SharingWithFamily,
  },
  "setting-personal-goals": {
    slug: "setting-personal-goals",
    title: "Setting a personal goal with your child",
    summary: "An optional minute or money target — a private nudge, not a leaderboard.",
    roles: ["parents"],
    component: SettingPersonalGoals,
  },
  "multiple-children": {
    slug: "multiple-children",
    title: "Adding more than one child",
    summary: "Same account, multiple children — even across different schools.",
    roles: ["parents"],
    component: MultipleChildren,
  },
  "leaderboards-and-privacy": {
    slug: "leaderboards-and-privacy",
    title: "Leaderboards and your child's privacy",
    summary: "What strangers can see, what other families can see, what's never shown.",
    roles: ["parents"],
    component: LeaderboardsAndPrivacy,
  },

  // ── Donor ────────────────────────────────────────────────
  "making-a-donation": {
    slug: "making-a-donation",
    title: "How to make a donation",
    summary: "Open the link a family shared with you, pick an amount, enter card details — done.",
    roles: ["donors"],
    component: MakingADonation,
  },
  "donating-without-an-account": {
    slug: "donating-without-an-account",
    title: "Donating without making an account",
    summary: "There's no signup. The receipt comes by email, and you can come back any time using the same link.",
    roles: ["donors"],
    component: DonatingWithoutAnAccount,
  },
  "covering-processing-fees": {
    slug: "covering-processing-fees",
    title: "Covering the processing fee",
    summary: "A small extra charge so 100% of your pledge actually reaches the school.",
    roles: ["donors"],
    component: CoveringProcessingFees,
  },
  "anonymous-donations-and-messages": {
    slug: "anonymous-donations-and-messages",
    title: "Anonymous donations and public messages",
    summary: "What's shown on the student's page when you donate, and how to keep your name off it.",
    roles: ["donors"],
    component: AnonymousDonationsAndMessages,
  },
  "donor-receipts-and-refunds": {
    slug: "donor-receipts-and-refunds",
    title: "Receipts and refunds",
    summary: "Where to find your receipt, and what to do if something went wrong.",
    roles: ["donors"],
    component: DonorReceiptsAndRefunds,
  },
};

export function getArticle(slug) {
  return ARTICLES[slug] || null;
}

export function articlesForRole(roleSlug) {
  return Object.values(ARTICLES).filter((a) => a.roles.includes(roleSlug));
}
